import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ReturnLayout } from '../../Components/ReturnLayout';
import ContentLoader from '../../Components/ContentLoader';
import { GET_USER } from '../../graphql/queries';
import type { GetUserQuery, Role } from '../../__generated__/graphql';
import { Alert, Badge, Col, ListGroup, Row } from 'react-bootstrap';

/**
 * Ce composant permet de consulter les informations liées à un utilisateur.
 * Ce composant est accessible à partir de l'URL `/administration/:id`.
 * @returns Le composant rendu par React
 */
export default function ReadUser(): React.ReactElement {
  const { id } = useParams();

  /**
   * Fonction de rendu pour les rôles de l'utilisateur ou de l'utilisatrice.
   *
   * @param roles Les rôles qui doivent être rendu.
   *
   * @returns Les rôles rendu.
   */
  const displayRoles = (roles: Role[] | undefined): React.ReactNode => {
    if (!roles) {
      return <></>;
    }
    if (roles.length === 0) {
      return (
        <Alert variant="warning">
          Aucun rôle n&apos;a été assigné à cet utilisateur
        </Alert>
      );
    }
    return roles.map<React.ReactElement>(r => (
      <ListGroup.Item key={r.name}>{r.readableForm}</ListGroup.Item>
    ));
  };

  /**
   * Fonction de rendu du contenu de la page, une fois les données chargées.
   *
   * @param data L'utilisateur à afficher.
   *
   * @returns La page actuelle, rendue avec les données.
   */
  const displayContent = useCallback(
    (data: GetUserQuery | undefined): React.ReactElement => {
      const user = data?.users?.items?.at(0);
      const roles = data?.roles;
      // skipcq JS-0424
      if (!user) return <></>;

      return (
        <>
          <Row>
            <Col>
              <div className="h2">
                {`Profil de ${user?.firstName} ${user?.lastName}`}
              </div>
            </Col>
          </Row>
          <Row>
            <hr />
          </Row>
          <Row>
            <Col md="auto">
              <ListGroup>
                <ListGroup.Item>{`Prénom : ${user?.firstName}`}</ListGroup.Item>
                <ListGroup.Item>
                  {`Nom de famille : ${user?.lastName}`}
                </ListGroup.Item>
                <ListGroup.Item>{`Courriel : ${user?.email}`}</ListGroup.Item>
                <ListGroup.Item>
                  État du compte :{' '}
                  <Badge bg={user?.state === 'Actif' ? 'success' : 'danger'}>
                    {user?.state}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item>
                  {new Date(user?.lockoutEnd).toISOString() <
                  new Date().toISOString()
                    ? 'Ce compte est déverouillé'
                    : `Ce compte est vérouillé jusqu'au ${new Date(
                        user?.lockoutEnd
                      ).toLocaleString()}`}
                </ListGroup.Item>
              </ListGroup>
              <div className="h3 mt-3">Rôles</div>
              <ListGroup>{displayRoles(roles)}</ListGroup>
            </Col>
          </Row>
        </>
      );
    },
    []
  );

  return (
    <ReturnLayout href="/administration">
      <ContentLoader
        query={GET_USER}
        variables={{ id }}
        data={displayContent}
      />
    </ReturnLayout>
  );
}
